import React from 'react'
import PropTypes from 'prop-types'
import DetailsTableHeader from './details/details_table_header'
import StrokeRow from './details/stroke_row'
import DetailsScroll from './details/details_scroll'
import LegendRow from './details/legend_row'
import { scorecardTotalDisplay } from '../utils/details_utils'
import { applyXesByHole } from '../utils/x_score_utils'
import { VIEW_MODES } from '../constants'

const toStrokeRowData = (agg) => {
  const scores = agg.netScores ?? []
  const toPar = agg.toParNet ?? []
  const totals = agg.totals?.netScores ?? {}
  const apiSums = agg.strokeSums ?? {}

  // Out / In / Tot come from the aggregate's stroke sums (running totals, the
  // same stroke_sum_front / stroke_sum_back / stroke_sum the details endpoint
  // and the player scorecard read; results-blob field pending API delivery).
  // Until a blob carries them, fall back to the blob totals:
  // the blob totals a nine only once every hole has a score, so a partly
  // played nine is blank on purpose. A row with no score at all (a no-show,
  // a withdrawal before teeing off) sums to 0 instead, like the player
  // scorecard and the legacy card.
  const hasAnyScore = scores.some(Boolean)
  const sumOrZero = (value) => value ?? (hasAnyScore ? '' : 0)

  return {
    strokesByHoleDisplay: applyXesByHole(scores, agg.xesByHole),
    pointsByHole: toPar,
    strikethroughsStrokesByHole: agg.strikethroughsStrokesByHole ?? [],
    strokeSumFront: apiSums.front ?? sumOrZero(totals.out),
    strokeSumBack: apiSums.back ?? sumOrZero(totals.in),
    strokeSum: scorecardTotalDisplay({ disposition: agg.disposition, strokeSum: apiSums.total ?? sumOrZero(totals.total) }),
    dispositionCause: agg.dispositionCause,
  }
}

const ExpandedScorecardsTable = React.forwardRef(({ scopes, holeByHoleView, viewMode }, ref) => {
  const aggregates = Object.values(scopes).flat()

  const firstAgg = aggregates.find((agg) => agg.netScores?.length > 0)
  if (!firstAgg) return null

  const nrHoles = firstAgg.netScores.length
  const holeLabels = Array.from({ length: nrHoles }, (_, i) => String(i + 1))

  return (
    <div className='lbv2__expanded_scorecards_table' ref={ref}>
      <DetailsScroll>
        <table className="lbv2__details-table">
          <DetailsTableHeader holeLabels={holeLabels} />
          <tbody>
            {aggregates.map((agg) => {
              if (!agg.netScores?.length) return null

              return (
                <StrokeRow
                  key={agg.id}
                  data={toStrokeRowData(agg)}
                  nrHoles={nrHoles}
                  label={agg.name}
                  holeByHoleView={holeByHoleView}
                />
              )
            })}
            {holeByHoleView === 'colors' && <LegendRow viewMode={viewMode} holeByHoleView={holeByHoleView} />}
          </tbody>
        </table>
      </DetailsScroll>
    </div>
  )
})

ExpandedScorecardsTable.displayName = 'ExpandedScorecardsTable'

ExpandedScorecardsTable.propTypes = {
  scopes: PropTypes.object.isRequired,
  holeByHoleView: PropTypes.string,
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)),
}

export default ExpandedScorecardsTable
